import { CommonActionTypes, DmViewerAPI, DmViewerContainer } from '@bimser/common';
import dmInitializerAction from '@bimser/common/dist/action/dmInitializerAction';
import { BCLoading, IButtonOverflowItem, findMessage } from '@bimser/components';
import { FormItemTypes, IDictionary, MLHelper } from '@bimser/core';
import IconEdit from '@bimser/icons/16/edit';
import { JSX } from 'react';
import Style from '../../../../assets/DocumentViewerStyle.scss';
import GetRelatedDocumentObjects from '../../../../services/getRelatedDocumentObjects';
import CompositeDataBoundControl from '../../base/CompositeDataBoundControl';
import IDocumentViewerProps from './entities/IDocumentViewerProps';
const DesignTemplate = require('../../../../assets/img/document-viewer.svg').default;

class DocumentViewer extends CompositeDataBoundControl {
	viewerId: string;
	height: number;
	targetPath: string;
	targetId: number;
	targetSecretKey: string;
	allowEdit: boolean;
	showOn: 'panel' | 'modal' | 'drawer';
	isDocumentLoading: boolean;

	__documentSecretKey: string;
	__ribbonItems: IButtonOverflowItem[];
	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.DocumentViewer;

		this.height = null;
		this.targetPath = null;
		this.targetId = null;
		this.targetSecretKey = null;
		this.allowEdit = false;
		this.showOn = 'panel';
		this.__reloadDocumentCallback = this.__reloadDocumentCallback.bind(this);
	}

	__onLoad() {
		super.__onLoad();

		if (this.targetId && !this.isDocumentLoading && this.__form.displayMode !== 'preview') {
			this.__proxyChanging.isDocumentLoading = true;

			GetRelatedDocumentObjects({
				projectName: this.__form.projectName,
				formName: this.__form.formName,
				parameters: {
					objectRequestSecretKeys: [this.targetSecretKey]
				},
				packageVersion: this.__form.packageVersion
			})
				.then(response => {
					const objects: any[] = response.data.objects;
					if (objects?.length) {
						this.__documentSecretKey = objects[0].secretKey;
						this.loadDocument({
							secretKey: this.__documentSecretKey,
							fileName: MLHelper.getMLText(objects[0].fileName ?? objects[0].name)
						});
					}
				})
				.finally(() => (this.__proxyChanging.isDocumentLoading = false));
		}
	}

	initialize(): void {
		super.initialize();
		this.viewerId = `${this.__form.projectName}_${this.__form.formName}_${this.name}_viewer`;
	}

	__loadProperties(data: IDocumentViewerProps) {
		super.__loadProperties(data);
		this.__proxyChanged.height = data.height;
		this.__proxyChanged.targetPath = data.targetPath;
		this.__proxyChanged.targetId = data.targetId;
		this.__proxyChanged.targetSecretKey = data.targetSecretKey;
		this.__proxyChanged.isDocumentLoading = data.isDocumentLoading;
		this.__proxyChanged.allowEdit = data.allowEdit;
		this.__proxyChanged.showOn = data.showOn;

		if (data.allowEdit) {
			this.__ribbonItems = [
				{
					key: 'edit',
					buttonProps: {
						children: findMessage.get('100412'),
						icon: <IconEdit />,
						onClick: e => {
							e.preventDefault();
							e.stopPropagation();
							this.__openEditDocument();
						},
						type: 'text'
					}
				}
			];
		}
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			height: this.height,
			targetPath: this.targetPath,
			targetId: this.targetId,
			showOn: this.showOn
		};
	}

	__reloadDocumentCallback() {
		DmViewerAPI.get(this.viewerId).remove(this.viewerId);
		this.loadDocument({
			secretKey: this.__documentSecretKey,
			fileName: this.targetPath.split('/').pop()
		});
	}

	__openEditDocument() {
		this.__form.emit(
			'dispatchAction',
			dmInitializerAction({
				onCompleted: () => {
					this.__form.emit('dispatchAction', {
						type: CommonActionTypes.RELATED_DOCUMENTS_VIEW_FILE,
						payload: {
							windowId: this.__form.__windowId,
							panelId: true,
							parentWindowType: this.__form.parentWindowType,
							secretKey: this.__documentSecretKey,
							fileName: this.targetPath.split('/').pop(),
							itemId: this.targetId,
							editable: true,
							reloadCallback: this.__reloadDocumentCallback,
							openOn: this.showOn,
							maskClosable: false
						}
					});
				}
			})
		);
	}

	/**
	 * Loads the document into the DocumentViewer.
	 * @param fileName
	 * Name of the file.
	 *
	 * @param secretKey
	 * Secret key of the document.
	 *
	 * @optional `versionSecretKey`
	 * versionSecretKey of the document.
	 */
	loadDocument(payload: { fileName: string; secretKey: string; versionSecretKey?: string }) {
		DmViewerAPI.get(this.viewerId).addNewFileAndLoad({
			viewerId: this.viewerId,
			fileName: payload.fileName,
			secretKey: payload.secretKey,
			versionSecretKey: payload.versionSecretKey ?? null,
			viewMode: 'view'
		});
	}

	__render(_children?: JSX.Element) {
		if (this.__form.displayMode == 'preview') {
			return super.__render(
				<div
					className={Style.DVContainer}
					style={{ height: this.height }}
				>
					<img
						src={DesignTemplate}
						height="100%"
						width="100%"
					/>
				</div>
			);
		}
		return super.__render(
			<div
				className={Style.DVContainer}
				style={{ height: this.height }}
			>
				<BCLoading show={this.isDocumentLoading}>
					{this.__documentSecretKey ? (
						<DmViewerContainer
							panelId={this.viewerId}
							ribbonBarItems={this.__ribbonItems}
						/>
					) : (
						<img
							src={DesignTemplate}
							height="100%"
							width="100%"
						/>
					)}
				</BCLoading>
			</div>
		);
	}
}
export default DocumentViewer;
